// src/web/lib/api.ts

import type { App } from '@api/index'
import { treaty } from '@elysiajs/eden'
import type { ApiError, ApiResponse } from '@shared/errors'
import { useWorkspaceStore } from '@/stores/workspace'
import { captureCsrfToken, ensureCsrfToken, isMutationMethod } from './csrf'

// Re-export error types for frontend use
export type { ApiError, ApiResponse }

// Create Eden client pointing to the API
// In dev, Vite proxies /api to the API server (see vite.config.ts)
// In prod, same origin serves both
// Using empty string or current origin lets the proxy handle routing
const apiUrl = window.location.origin

export const api = treaty<App>(apiUrl, {
	// Don't revive date-like strings into Date objects: the API schemas type all
	// timestamps as strings, and revival also corrupts user data that merely looks
	// like a date (e.g. a folder literally named "2026-06-11").
	parseDate: false,
	fetch: {
		credentials: 'include', // Include cookies for auth
	},
	headers: (_path, _options) => {
		const workspaceId = useWorkspaceStore.getState().currentWorkspaceId
		return workspaceId ? { 'X-Workspace-Id': workspaceId } : undefined
	},
	// Attach the CSRF token on state-changing requests (the server enforces it).
	// onRequest is async, so a cold cache fetches a token before the first mutation.
	onRequest: async (_path, options) => {
		if (!isMutationMethod(options.method)) return
		const token = await ensureCsrfToken(`${apiUrl}/api`)
		if (token) return { headers: { 'x-csrf-token': token } }
	},
	// Refresh the cached token from every response that carries one.
	onResponse: (response) => {
		captureCsrfToken(response.headers.get('x-csrf-token'))
	},
})

// Helper to handle API errors with proper typing
export function handleApiError(error: unknown): string {
	// Handle Eden Treaty error format
	if (error && typeof error === 'object') {
		if ('value' in error && error.value && typeof error.value === 'object') {
			const value = error.value as Record<string, unknown>

			// Check for standardized API error response
			if ('error' in value && value.error && typeof value.error === 'object') {
				const apiError = value.error as ApiError
				if (apiError.message) {
					return apiError.message
				}
			}

			// Fallback to legacy error formats
			if ('error' in value && typeof value.error === 'string') {
				return value.error
			}
			if ('message' in value && typeof value.message === 'string') {
				return value.message
			}
		}
	}

	return 'An unexpected error occurred'
}

// Helper to extract error code from API error
export function getApiErrorCode(error: unknown): string | null {
	if (error && typeof error === 'object') {
		if ('value' in error && error.value && typeof error.value === 'object') {
			const value = error.value as Record<string, unknown>
			if ('error' in value && value.error && typeof value.error === 'object') {
				const apiError = value.error as ApiError
				return apiError.code || null
			}
		}
	}
	return null
}

// Helper to check if API response has error
export function isApiError(response: unknown): response is ApiResponse & { success: false } {
	if (response && typeof response === 'object') {
		if ('value' in response && response.value && typeof response.value === 'object') {
			const value = response.value as Record<string, unknown>
			return 'success' in value && value.success === false && 'error' in value
		}
	}
	return false
}
